import { createContext, useContext, type ReactNode } from "react";
import { NavLink, Outlet, useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, History, LogOut, Search, Settings2, ShieldCheck, UserRound } from "lucide-react";
import type { Me } from "../../shared/contracts.js";
import { api } from "../api.js";
import { cx } from "../lib/cx.js";
import { initials } from "../lib/format.js";
import { ROLE_LABELS } from "../lib/labels.js";
import { clearContext } from "../lib/search-context.js";
import { meQuery } from "../queries.js";
import { Splash, Wordmark } from "./Brand.js";
import { Menu } from "./ui/Menu.js";
import { useToast } from "./ui/Toast.js";

const MeContext = createContext<Me | null>(null);

/** Sesión actual. Solo se usa bajo <AppShell>, que garantiza que existe. */
export function useMe(): Me {
  const me = useContext(MeContext);
  if (!me) throw new Error("useMe() fuera de <AppShell>");
  return me;
}

export function useIsAdmin(): boolean {
  return useMe().user.role === "admin";
}

/** Cierre de sesión: revoca en el servidor y borra TODO lo que quedó en memoria. */
export function useLogout() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  return useMutation({
    mutationFn: () => api.auth.logout(),
    onSettled: (_data, err) => {
      // Aunque el servidor falle, localmente se olvida todo: la cookie vence sola.
      qc.clear();
      clearContext();
      if (err) toast.error("No se pudo confirmar el cierre en el servidor; la sesión vencerá automáticamente.");
      void navigate("/login", { replace: true });
    },
  });
}

function NavItem({ to, icon, children }: { to: string; icon: ReactNode; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cx(
          "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors [&>svg]:size-4",
          isActive ? "bg-accent-soft text-accent-soft-fg" : "text-muted hover:bg-surface-2 hover:text-fg",
        )
      }
    >
      {icon}
      {children}
    </NavLink>
  );
}

export function AppShell() {
  const { data: me } = useQuery(meQuery);
  const logout = useLogout();
  const navigate = useNavigate();

  if (!me) return <Splash />;
  const isAdmin = me.user.role === "admin";

  const nav = (
    <>
      <NavItem to="/buscar" icon={<Search aria-hidden="true" />}>
        Buscar
      </NavItem>
      <NavItem to="/historial" icon={<History aria-hidden="true" />}>
        Historial
      </NavItem>
      {isAdmin && (
        <NavItem to="/admin" icon={<Settings2 aria-hidden="true" />}>
          Administración
        </NavItem>
      )}
    </>
  );

  return (
    <MeContext.Provider value={me}>
      <a
        href="#contenido"
        className="sr-only z-50 rounded-md bg-accent px-3 py-2 text-accent-fg focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
      >
        Saltar al contenido
      </a>
      <div className="flex min-h-dvh flex-col">
        <header className="sticky top-0 z-30 border-b border-border bg-surface/95 backdrop-blur supports-[backdrop-filter]:bg-surface/85">
          <div className="mx-auto flex h-14 max-w-[1600px] items-center gap-3 px-3 sm:px-5">
            <NavLink to="/buscar" aria-label="SENTINEL, ir a Buscar" className="rounded-md">
              <Wordmark />
            </NavLink>
            <nav aria-label="Principal" className="ml-4 hidden items-center gap-1 md:flex">
              {nav}
            </nav>
            <div className="ml-auto flex min-w-0 items-center gap-3">
              <span
                className="hidden min-w-0 items-center gap-1.5 truncate text-[13px] text-muted lg:inline-flex"
                title={me.institution ? `Institución: ${me.institution.name}` : "Administración de la plataforma"}
              >
                {me.institution ? (
                  <Building2 className="size-4 shrink-0" aria-hidden="true" />
                ) : (
                  <ShieldCheck className="size-4 shrink-0" aria-hidden="true" />
                )}
                <span className="truncate">{me.institution?.name ?? "Administración de la plataforma"}</span>
              </span>
              <Menu
                triggerLabel={`Menú de usuario de ${me.user.fullName}`}
                triggerClassName="inline-flex items-center gap-2 rounded-full py-1 pr-1 pl-1 hover:bg-surface-2 sm:pr-2.5"
                trigger={
                  <>
                    <span
                      aria-hidden="true"
                      className="grid size-8 place-items-center rounded-full bg-accent-soft text-[12px] font-semibold text-accent-soft-fg"
                    >
                      {initials(me.user.fullName)}
                    </span>
                    <span className="hidden max-w-40 truncate text-sm font-medium text-fg sm:inline">{me.user.fullName}</span>
                  </>
                }
                header={
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-fg">{me.user.fullName}</p>
                    <p className="truncate text-subtle">{me.user.email}</p>
                    <p className="mt-1 truncate text-muted">
                      {ROLE_LABELS[me.user.role]}
                      {me.institution ? ` · ${me.institution.name}` : ""}
                    </p>
                  </div>
                }
                items={[
                  {
                    key: "account",
                    label: "Mi cuenta",
                    icon: <UserRound aria-hidden="true" />,
                    onSelect: () => void navigate("/cuenta"),
                  },
                  "separator",
                  {
                    key: "logout",
                    label: logout.isPending ? "Cerrando sesión…" : "Cerrar sesión",
                    icon: <LogOut aria-hidden="true" />,
                    disabled: logout.isPending,
                    onSelect: () => logout.mutate(),
                  },
                ]}
              />
            </div>
          </div>
          <nav aria-label="Principal (móvil)" className="relative flex gap-1 overflow-x-auto border-t border-border px-2 py-1.5 md:hidden">
            {nav}
          </nav>
        </header>
        <main id="contenido" tabIndex={-1} className="mx-auto w-full max-w-[1600px] flex-1 px-3 py-4 outline-none sm:px-5 sm:py-6">
          <Outlet />
        </main>
        <footer className="border-t border-border px-3 py-3 text-center text-[12px] text-subtle sm:px-5">
          Uso restringido a la finalidad declarada en cada búsqueda (Ley 21.719). Toda consulta queda registrada.
        </footer>
      </div>
    </MeContext.Provider>
  );
}
